import {useQuery} from "react-query";
import {
    ACHIEVEMENTS,AWARDS,DASH_USERS,deleteElem,download,
    ENROLLMENTS,
    get,MESSAGES,post,put,
    SENSORS,
    SESSIONS,
    SMARTPHONES,
    USERS
} from "./Client";

export function createAdmin(newAdmin){
    return post(`${USERS}/admin`, {body: newAdmin})
}

export function editUser(uid, field, value) {
    return put(`${USERS}/${uid}`, {body: {[field]: value}})
}

export function deleteUser(uid) {
    return deleteElem(`${USERS}/${uid}`)
}

// La griglia MUI passa i filtri come JSON in base64. btoa accetta solo Latin-1: un filtro con
// "ò" o con caratteri non latini lo farebbe esplodere, quindi prima si codifica in UTF-8.
export function encodeTable(table) {
    return btoa(unescape(encodeURIComponent(JSON.stringify(table))))
}

// Tabella Utenti dell'admin, paginata lato server: sostituisce la copia completa in IndexedDB.
export function useGetUsersPaged(json) {
    const {status, data, error, isFetching} = useQuery([DASH_USERS, {json}], () => get(DASH_USERS, {params: {json}}), {keepPreviousData: true});
    return {status, page: data, error, isFetching};
}

// Destinatari di "invia a tutti i filtrati": solo utenti attivi, senza paginazione.
export function getUserTargets(json) {
    return get(DASH_USERS, {elem: "targets", params: {json}})
}

export function resolveUsers(values) {
    return post(DASH_USERS, {elem: "resolve", body: values}).then(({data}) => data)
}

export function isUsernameAvailable(username, uid) {
    return get(DASH_USERS, {elem: "username-available", params: {username, uid}})
}

export function exportUsers(json) {
    return download(`${DASH_USERS}/export`, {params: {json}})
}

export function useGetOrganizationUsers(oid, active=false) {
    const {
        status,
        data,
        error
    } = useQuery([USERS, {oid: oid}], () => get(USERS, {params: {oid: oid, active}}), {cacheTime: 10 * 60 * 1000});
    return {status, users: data || [], error};
}

export function useGetProfileUser(uid) {
    const {status, data, error} = useQuery([USERS, {user: uid}], () => get(USERS, {elem: uid}));
    return {status, user: data || {}, error};
}

// Risolve un utente a partire dall'indirizzo email: usato per entrare nella scheda
// del pinbiker partendo dai contatti dell'assistenza, dove l'unica chiave nota è l'email.
export function useGetProfileUserByEmail(email) {
    const {status, data, error} = useQuery(
        [USERS, {email}, "profile"],
        () => get(USERS, {elem: `${encodeURIComponent(email)}/profile`}),
        {enabled: !!email, retry: false}
    );
    return {status, profile: data || {}, error};
}

export function useGetUserDashboard(uid) {
    const {
        status,
        data,
        error
    } = useQuery([`${USERS}-dashboard`, {user: uid}], () => get(`${USERS}/${uid}/dashboard`), {
        staleTime: 60 * 1000,
        refetchOnMount: false
    });
    return {status, dashboard: data || {}, error};
}

export function useGetProfileUserSensors(uid) {
    const {
        status,
        data,
        error
    } = useQuery([USERS, {user: uid}, SENSORS], () => get(USERS, {elem: uid + "/" + SENSORS}));
    return {status, sensors: data || [], error};
}

export function useGetProfileUserSmartphones(uid) {
    let {
        status,
        data,
        error
    } = useQuery([USERS, {user: uid}, SMARTPHONES], () => get(USERS, {elem: uid + "/" + SMARTPHONES}));
    return {status, smartphones: data || [], error};
}

export function useGetProfileUserEnrollments(uid, enabled = true) {
    const {
        status,
        data,
        error
    } = useQuery([USERS, {user: uid}, ENROLLMENTS], () => get(USERS, {elem: uid + "/" + ENROLLMENTS}), {enabled});
    return {status, enrollments: data || [], error};
}

export function useGetProfileUserAchievements(uid) {
    const {
        status,
        data,
        error
    } = useQuery([USERS, {user: uid}, ACHIEVEMENTS], () => get(USERS, {elem: uid + "/" + ACHIEVEMENTS, params: {active: false}}));
    return {status, achievements: data || [], error};
}

export function useGetProfileUserAwards(uid) {
    const {
        status,
        data,
        error
    } = useQuery([USERS, {user: uid}, AWARDS], () => get(USERS, {elem: `${uid}/${AWARDS}`}));
    return {status, awards: data || [], error};
}

export function useGetProfileUserMessages(uid) {
    const {
        status,
        data,
        error
    } = useQuery([USERS, {user: uid}, MESSAGES], () => get(USERS, {elem: `${uid}/${MESSAGES}`}));
    return {status, messages: data || [], error};
}

export function useGetUserSessions(uid) {
    const {
        status,
        data,
        error
    } = useQuery([SESSIONS, USERS, {uid}], () => get(USERS, {elem: `${uid}/${SESSIONS}-overview`}));
    return {status, sessions: data, error};
}

export function useGetUserIntervalSessions(uid, start, end) {
    const {status, data, error} = useQuery([SESSIONS, USERS, {uid}, {
        start,
        end
    }], () => get(USERS, {elem: `${uid}/${SESSIONS}-overview`, params: {start, end}}), {enabled: !!start && !!end});
    return {status, sessions: data, error};
}

export function useGetUserVerifiedSessions(uid) {
    const {status, data, error} = useQuery([SESSIONS, USERS, {uid}], 
        () => get(USERS, {elem: `${uid}/${SESSIONS}-verified`}));
    return {status, verifiedSessions: data, error};
}

export function useGetAddressHistory(uid, url) {
    const {
        status,
        data,
        error
    } = useQuery([url, {uid}], () => get(USERS, {elem: uid + "/" + url}), {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false
    });
    return {status, addresses: data, error};
}
// Link condivisibile della posizione del pedale (uno solo attivo). 204 = nessun link: link resta null.
export const PEDAL_SHARE_LINK = "ugo/me/share-link";

export function useGetPedalShareLink(enabled = true) {
    const {status, data, error} = useQuery([PEDAL_SHARE_LINK], () => get(PEDAL_SHARE_LINK), {enabled, retry: false});
    return {status, link: data && data.url ? data : null, error};
}

export function createPedalShareLink(values) {
    return post(PEDAL_SHARE_LINK, {body: values});
}

export function revokePedalShareLink() {
    return deleteElem(PEDAL_SHARE_LINK);
}

// Pannello del pedale UGO (components/pedal/PedalBoard): il pinbiker legge il proprio da ugo/me, l'admin
// quello di un utente. Device e allarmi vengono dal server UGO.
export const loadOwnPedalBoard = () => get("ugo/me/board");
// Stessa chiave che usa PedalBoard col queryKey ["me-pedal"]: la pagina sa se ci sono pedali senza una chiamata in piu'.
export function useGetOwnPedalBoard() {
    const {status, data} = useQuery(["me-pedal", "board"], loadOwnPedalBoard,
        {refetchInterval: 60 * 1000, refetchOnWindowFocus: false, retry: false});
    return {status, devices: data?.devices || []};
}
export const loadOwnPedalAlert = (imei, id) =>
    get(`ugo/me/alerts/${encodeURIComponent(imei)}/${encodeURIComponent(id)}`);
export const loadUserPedalBoard = uid => () => get(`ugo/users/${uid}/board`);
export const loadUserPedalAlert = uid => (imei, id) =>
    get(`ugo/users/${uid}/alerts/${encodeURIComponent(imei)}/${encodeURIComponent(id)}`);
